/**
 * TanStack Query hooks — Quote Requests (Chunk 9.L.6 — broker UI).
 *
 * Backend :
 *   - apps/api/src/routes/quote-requests.ts (chunks 9.F lecture + 9.L.1 send)
 *       GET    /v1/quote-requests?project_id&status&cursor&limit   (9.F, scope projects:read)
 *       GET    /v1/quote-requests/:id                              (9.F, scope projects:read)
 *       POST   /v1/quote-requests/:id/send                         (9.L.1, scope submissions:write per-route)
 *
 * Wire shapes confirmées 9.L.6 diagnostic :
 *   - LIST : `data = { items: QuoteRequestRow[], next_cursor: string|null }`
 *     (recipients NON inclus dans la liste — perf)
 *   - DETAIL : `data = QuoteRequestRow + { recipients: QuoteRequestRecipientRow[] }`
 *     (nested via `QuoteRequestWithRecipientsSchema` côté backend)
 *   - SEND : `data = QuoteRequestRow + { recipients: QuoteRequestRecipientRow[] }`
 *     (re-SELECT post-update, parité GET /:id)
 *
 * `api.get/post` unwrap `body.data` automatiquement (apps/web/src/lib/api.ts:87)
 * → les hooks reçoivent directement la structure data.
 *
 * Codes erreur attendus côté UI (tous bilingues) :
 *   QUOTE_REQUESTS_NOT_FOUND (404) | QUOTE_REQUESTS_INVALID_TRANSITION (409)
 *   QUOTE_REQUESTS_NO_RECIPIENTS (422) | QUOTE_REQUESTS_EMAIL_SEND_FAILED (500)
 *   GATING_HUMAN_REQUIRED (403, broker JWT bypass naturel donc rare en pratique humaine)
 *   VALIDATION_ERROR (400) sur expected_response_by non-ISO datetime
 *
 * Convention queryKey :
 *   ['quote-requests', 'by-project', { projectId, cursor, limit, status }]
 *   ['quote-requests', 'detail', id]
 *
 * Invalidation :
 *   - useSendQuoteRequest → invalidate ['quote-requests', 'by-project'] +
 *     ['quote-requests', 'detail', id] (status passe à 'sent', recipients mis à jour)
 */

import type {
  AddQuoteRequestRecipientInput,
  CancelQuoteRequestInput,
  CreateQuoteRequestInput,
  QuoteRequestRecipientRow,
  QuoteRequestListItem,
  QuoteRequestRow,
  QuoteRequestStatus,
  SendQuoteRequestInput,
  SendReadiness,
  UpdateQuoteRequestInput,
  UpdateQuoteRequestRecipientInput,
} from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

import { shouldPollRecipientStatuses } from "./quoteRequestPolling";

// -----------------------------------------------------------------------------
// Types — re-export ergonomiques
// -----------------------------------------------------------------------------

/** QR avec recipients nested — shape de GET /:id et POST /:id/send. */
export type QuoteRequestWithRecipientsRow = QuoteRequestRow & {
  recipients: QuoteRequestRecipientRow[];
};

export type QuoteRequestListResult = {
  items: QuoteRequestListItem[];
  next_cursor: string | null;
};

export type QuoteRequestsByProjectParams = {
  projectId: string | undefined;
  /** Défaut backend = pas de filtre (toutes les status). */
  status?: QuoteRequestStatus;
  cursor?: string | null;
  limit?: number;
  /** Permet de désactiver la requête tant que `projectId` n'est pas connu. */
  enabled?: boolean;
};

// -----------------------------------------------------------------------------
// GET /v1/quote-requests — liste filtrée par project_id
// -----------------------------------------------------------------------------

function buildListPath(params: QuoteRequestsByProjectParams): string {
  const sp = new URLSearchParams();
  if (params.projectId) sp.set("project_id", params.projectId);
  if (params.status) sp.set("status", params.status);
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  return qs ? `/v1/quote-requests?${qs}` : `/v1/quote-requests`;
}

/**
 * Les demandes en cours du cabinet — 9.BD.1.
 *
 * Dix-neuf brouillons dormaient en pré-prod, invisibles partout sauf sur la
 * fiche de leur dossier : un conseiller qui quittait `/preparer` ne savait plus
 * où retrouver ce qu'il avait commencé, et « Nouvelle demande » lui donnait
 * l'impression de repartir de zéro.
 *
 * **Aucun filtre par utilisateur** : une demande appartient au cabinet, pas à
 * celui qui l'a ouverte. Un collègue doit pouvoir reprendre un dossier commencé
 * par un autre — c'est le sens d'un portefeuille partagé.
 */
export function useQuoteRequestsEnCours(params: { limit?: number; enabled?: boolean } = {}) {
  const limit = params.limit ?? 5;
  return useQuery({
    queryKey: ["quote-requests", "en-cours", { limit }],
    queryFn: () =>
      api.get<QuoteRequestListResult>(`/v1/quote-requests?status=draft&limit=${limit}`),
    enabled: params.enabled ?? true,
  });
}

export function useQuoteRequestsByProject(params: QuoteRequestsByProjectParams) {
  return useQuery({
    queryKey: [
      "quote-requests",
      "by-project",
      {
        projectId: params.projectId,
        status: params.status,
        cursor: params.cursor,
        limit: params.limit,
      },
    ],
    queryFn: () => api.get<QuoteRequestListResult>(buildListPath(params)),
    enabled: (params.enabled ?? true) && !!params.projectId,
  });
}

// -----------------------------------------------------------------------------
// GET /v1/quote-requests/:id — détail + recipients nested
// -----------------------------------------------------------------------------

/**
 * Intervalle de polling (ms) des statuts recipients après un Send.
 * 9.T.5.v3.7 post-smoke : le worker email-outbox (timer staging ~2 min)
 * bascule `pending → sent` de façon ASYNCHRONE, APRÈS l'invalidation faite au
 * moment du Send. Sans polling, l'UI reste bloquée sur « En attente » jusqu'à
 * un refresh manuel. On re-fetch le détail tant qu'au moins un recipient est
 * « délivrable-en-attente » (`pending` AVEC email → le worker le traitera),
 * puis on arrête (tous terminaux, OU `pending` sans email = zero-deliverable
 * non traité par le worker → pas de polling infini). Idiome parité
 * `useExtractionsByParent` (refetchInterval conditionnel sur état terminal).
 */
const RECIPIENT_POLL_MS = 5000;

export function useQuoteRequest(id: string | undefined) {
  return useQuery({
    queryKey: ["quote-requests", "detail", id],
    queryFn: () => api.get<QuoteRequestWithRecipientsRow>(`/v1/quote-requests/${id}`),
    enabled: !!id,
    // 9.T.5.v3.7 post-smoke — auto-refresh des statuts recipients après Send.
    // Le worker email-outbox bascule `pending → sent` de façon asynchrone (timer
    // staging ~2 min) APRÈS l'invalidation faite au Send ; sans polling, l'UI
    // reste sur « En attente » jusqu'à un refresh manuel. Prédicat pur isolé
    // (`quoteRequestPolling.ts`) : stop dès que plus aucun recipient délivrable-
    // en-attente (anti-polling-infini sur zero-deliverable).
    refetchInterval: (query) => {
      const data = query.state.data as QuoteRequestWithRecipientsRow | undefined;
      if (!data) return false;
      return shouldPollRecipientStatuses(data.recipients ?? []) ? RECIPIENT_POLL_MS : false;
    },
    refetchOnWindowFocus: false,
  });
}

// -----------------------------------------------------------------------------
// POST /v1/quote-requests/:id/send — Chunk 9.L.1 backend
// -----------------------------------------------------------------------------

export type SendQuoteRequestMutationVars = {
  /** UUID du QR à envoyer. */
  quoteRequestId: string;
  /** Optionnel — `expected_response_by` ISO datetime. NULL/absent = pas de reset. */
  input?: SendQuoteRequestInput;
  /** Optionnel — pour invalider la liste projet après envoi. */
  projectId?: string;
};

/**
 * Send une quote request à ses recipients pending.
 *
 * Le backend (9.L.1) :
 *   - Transition `status` `draft → sent` ou `sent → sent` (idempotency partial-send retry).
 *   - Envoie email à chaque recipient `status=pending` (mode `log` staging par défaut).
 *   - Skip already-sent recipients (no duplicate email).
 *   - Si all-fail → ROLLBACK + 500 EMAIL_SEND_FAILED.
 *   - Default gating policy 9.H.5 `agent_allowed=false` (broker JWT bypass naturel).
 *
 * onSuccess invalide :
 *   - `['quote-requests', 'detail', quoteRequestId]` (status + recipients à jour)
 *   - `['quote-requests', 'by-project']` (liste projet refresh)
 */
export function useSendQuoteRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ quoteRequestId, input }: SendQuoteRequestMutationVars) =>
      api.post<QuoteRequestWithRecipientsRow>(
        `/v1/quote-requests/${quoteRequestId}/send`,
        (input ?? {}) as unknown as Record<string, unknown>,
      ),
    onSuccess: (_data, { quoteRequestId }) => {
      void qc.invalidateQueries({
        queryKey: ["quote-requests", "detail", quoteRequestId],
      });
      // 9.BJ.1 — la conformité d'envoi dépend de ce qui vient de changer.
      void qc.invalidateQueries({ queryKey: ["quote-requests", "readiness", quoteRequestId] });
      void qc.invalidateQueries({ queryKey: ["quote-requests", "by-project"] });
      // 9.BE.1 — une demande ENVOYÉE quitte « Demandes en cours ».
      void qc.invalidateQueries({ queryKey: ["quote-requests", "en-cours"] });
    },
  });
}

// =============================================================================
// CRUD write mutations — Chunk 9.L.8 broker UI lifecycle
//
// Routes backend (9.L.7) :
//   POST   /v1/quote-requests                              create draft + recipients?
//   PATCH  /v1/quote-requests/:id                          update title/expected_response_by
//   POST   /v1/quote-requests/:id/cancel                   transition → cancelled (gating actif)
//   POST   /v1/quote-requests/:id/recipients               add recipient
//   DELETE /v1/quote-requests/:id/recipients/:recipientId  remove recipient pending
//
// Invalidation queryKey commune :
//   ['quote-requests', 'by-project'] + ['quote-requests', 'detail', quoteRequestId]
// =============================================================================

/** Vars POST /v1/quote-requests — create QR draft + recipients optionnels. */
export type CreateQuoteRequestMutationVars = {
  input: CreateQuoteRequestInput;
};

export function useCreateQuoteRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ input }: CreateQuoteRequestMutationVars) =>
      api.post<QuoteRequestWithRecipientsRow>(
        `/v1/quote-requests`,
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ["quote-requests", "by-project"] });
      // Pre-seed detail cache pour éviter un GET supplémentaire à l'ouverture
      // immédiate (broker UX naturelle "j'ai créé, je veux voir").
      qc.setQueryData(["quote-requests", "detail", data.id], data);
    },
  });
}

/** Vars PATCH /v1/quote-requests/:id — update broker editable metadata. */
export type UpdateQuoteRequestMutationVars = {
  quoteRequestId: string;
  input: UpdateQuoteRequestInput;
};

export function useUpdateQuoteRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ quoteRequestId, input }: UpdateQuoteRequestMutationVars) =>
      api.patch<QuoteRequestWithRecipientsRow>(
        `/v1/quote-requests/${quoteRequestId}`,
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: (_data, { quoteRequestId }) => {
      void qc.invalidateQueries({
        queryKey: ["quote-requests", "detail", quoteRequestId],
      });
      // 9.BJ.1 — la conformité d'envoi dépend de ce qui vient de changer.
      void qc.invalidateQueries({ queryKey: ["quote-requests", "readiness", quoteRequestId] });
      void qc.invalidateQueries({ queryKey: ["quote-requests", "by-project"] });
    },
  });
}

/** Vars POST /v1/quote-requests/:id/cancel — gating Couche 2 actif (`quote_request.cancel`). */
export type CancelQuoteRequestMutationVars = {
  quoteRequestId: string;
  input: CancelQuoteRequestInput;
};

export function useCancelQuoteRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ quoteRequestId, input }: CancelQuoteRequestMutationVars) =>
      api.post<QuoteRequestWithRecipientsRow>(
        `/v1/quote-requests/${quoteRequestId}/cancel`,
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: (_data, { quoteRequestId }) => {
      void qc.invalidateQueries({
        queryKey: ["quote-requests", "detail", quoteRequestId],
      });
      // 9.BJ.1 — la conformité d'envoi dépend de ce qui vient de changer.
      void qc.invalidateQueries({ queryKey: ["quote-requests", "readiness", quoteRequestId] });
      void qc.invalidateQueries({ queryKey: ["quote-requests", "by-project"] });
      // 9.BE.1 — une demande ABANDONNÉE doit elle aussi quitter « Demandes en
      // cours ». L'invalidation vit dans la mutation et non chez l'appelant : le
      // geste existe à deux endroits, et un appelant qui l'oublierait laisserait
      // un brouillon fantôme — présent dans la liste, annulé côté serveur.
      void qc.invalidateQueries({ queryKey: ["quote-requests", "en-cours"] });
    },
  });
}

/** Vars POST /v1/quote-requests/:id/recipients — add recipient (no gating Phase 1). */
export type AddRecipientMutationVars = {
  quoteRequestId: string;
  input: AddQuoteRequestRecipientInput;
};

export function useAddQuoteRequestRecipient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ quoteRequestId, input }: AddRecipientMutationVars) =>
      api.post<QuoteRequestRecipientRow>(
        `/v1/quote-requests/${quoteRequestId}/recipients`,
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: (_data, { quoteRequestId }) => {
      void qc.invalidateQueries({
        queryKey: ["quote-requests", "detail", quoteRequestId],
      });
      // 9.BJ.1 — la conformité d'envoi dépend de ce qui vient de changer.
      void qc.invalidateQueries({ queryKey: ["quote-requests", "readiness", quoteRequestId] });
      void qc.invalidateQueries({ queryKey: ["quote-requests", "by-project"] });
    },
  });
}

/**
 * Vars PATCH `/v1/quote-requests/:id/recipients/:recipientId` (9.T.5.v3.7).
 * Gating Couche 2 actif — `quote_request.update_recipient` seedée 0049
 * `agent_allowed=false` par défaut (broker-only Phase 1).
 */
export type UpdateRecipientMutationVars = {
  quoteRequestId: string;
  recipientId: string;
  input: UpdateQuoteRequestRecipientInput;
};

export function useUpdateQuoteRequestRecipient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ quoteRequestId, recipientId, input }: UpdateRecipientMutationVars) =>
      api.patch<QuoteRequestRecipientRow>(
        `/v1/quote-requests/${quoteRequestId}/recipients/${recipientId}`,
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: (_data, { quoteRequestId }) => {
      void qc.invalidateQueries({
        queryKey: ["quote-requests", "detail", quoteRequestId],
      });
      // 9.BJ.1 — la conformité d'envoi dépend de ce qui vient de changer.
      void qc.invalidateQueries({ queryKey: ["quote-requests", "readiness", quoteRequestId] });
      void qc.invalidateQueries({ queryKey: ["quote-requests", "by-project"] });
    },
  });
}

/** Vars DELETE /v1/quote-requests/:id/recipients/:recipientId — hard DELETE recipient pending. */
export type RemoveRecipientMutationVars = {
  quoteRequestId: string;
  recipientId: string;
};

/**
 * Remove recipient pending. **Callout C3 9.L.7 staging** : la response DELETE
 * shape est `{id}` minimaliste (parité 9.J), pas le row complet. On invalide
 * le cache QR detail post-mutation pour rafraîchir la liste recipients.
 */
export function useRemoveQuoteRequestRecipient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ quoteRequestId, recipientId }: RemoveRecipientMutationVars) =>
      api.del<{ id: string }>(`/v1/quote-requests/${quoteRequestId}/recipients/${recipientId}`),
    onSuccess: (_data, { quoteRequestId }) => {
      void qc.invalidateQueries({
        queryKey: ["quote-requests", "detail", quoteRequestId],
      });
      // 9.BJ.1 — la conformité d'envoi dépend de ce qui vient de changer.
      void qc.invalidateQueries({ queryKey: ["quote-requests", "readiness", quoteRequestId] });
      void qc.invalidateQueries({ queryKey: ["quote-requests", "by-project"] });
    },
  });
}

// -----------------------------------------------------------------------------
// Le cahier de charges d'une demande — chunks 9.AX.1 / 9.AY.1
// -----------------------------------------------------------------------------
//
// Vocabulaire : côté écran, on parle de **cahier de charges**. Côté contrat, c'est
// une `form_response` rattachée à la demande et l'artefact PDF qui en découle. Le
// conseiller n'a pas à connaître le second — mais le lien est ici, une fois, pour
// qui lit ce fichier.

/** Ce que la demande demande, tel que l'API le rend. */
export type QuoteRequestSpecRow = {
  id: string;
  quote_request_id: string;
  form_response_id: string;
  /** Le PDF produit. `null` seulement si le rendu a échoué. */
  artifact_file_id: string | null;
  position: number;
  rendered_at: string | null;
  created_at: string;
};

/**
 * 9.BJ.1 — la conformité d'envoi, telle que le SERVEUR la juge.
 *
 * La même évaluation que celle qui fait refuser `POST /:id/send`. L'écran ne
 * recalcule donc rien : il ne peut ni promettre un envoi que le serveur
 * refuserait, ni interdire un envoi qu'il accepterait.
 *
 * Pendant une attente client du dossier, useProjectCompleteness invalide cette
 * lecture après son rafraîchissement. Le verdict reste intégralement serveur.
 * Le réglage global de refetch au focus n'est pas modifié ici.
 */
export function useSendReadiness(quoteRequestId: string | undefined) {
  return useQuery({
    queryKey: ["quote-requests", "readiness", quoteRequestId],
    queryFn: () => api.get<SendReadiness>(`/v1/quote-requests/${quoteRequestId}/readiness`),
    enabled: !!quoteRequestId,
  });
}

/**
 * Produire l'effectif transmis — chunk 9.BK.3.
 *
 * **Un geste explicite.** Rien ne le déclenche automatiquement : un envoi
 * assureur ne doit pas gagner une pièce à l'insu de celui qui l'envoie.
 *
 * On invalide la readiness au succès : c'est elle qui porte l'état, et la
 * laisser périmée ferait mentir l'écran juste après le geste qui l'a corrigé.
 */
export function useGenererExportEffectif(quoteRequestId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ file_id: string; file_name: string; size_bytes: number; lignes: number }>(
        `/v1/quote-requests/${quoteRequestId}/census-export`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote-requests", "readiness", quoteRequestId] });
      // Le classeur apparaît aussi dans les documents du dossier.
      void qc.invalidateQueries({ queryKey: ["files"] });
    },
  });
}

export function useQuoteRequestSpecs(quoteRequestId: string | undefined) {
  return useQuery({
    queryKey: ["quote-requests", "spec", quoteRequestId],
    queryFn: () =>
      api.get<{ items: QuoteRequestSpecRow[] }>(`/v1/quote-requests/${quoteRequestId}/spec`),
    enabled: !!quoteRequestId,
    refetchOnWindowFocus: false,
  });
}

/**
 * Rattache un cahier de charges à la demande, et produit son document.
 *
 * Le serveur refuse une réponse encore en brouillon, d'un autre dossier, ou une
 * demande déjà partie. On ne rejoue aucune de ces règles ici.
 */
export function useAttachQuoteRequestSpec() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      quoteRequestId,
      formResponseId,
    }: {
      quoteRequestId: string;
      formResponseId: string;
    }) =>
      api.post<QuoteRequestSpecRow>(`/v1/quote-requests/${quoteRequestId}/spec`, {
        form_response_id: formResponseId,
      }),
    onSuccess: (_d, { quoteRequestId }) => {
      void qc.invalidateQueries({ queryKey: ["quote-requests", "spec", quoteRequestId] });
      void qc.invalidateQueries({ queryKey: ["quote-requests", "detail", quoteRequestId] });
      void qc.invalidateQueries({ queryKey: ["quote-requests", "readiness", quoteRequestId] });
    },
  });
}

/**
 * Refait le document quand le cahier de charges a changé depuis son rendu.
 *
 * Produit un **nouveau** fichier : l'ancien reste la trace de ce qui avait été
 * préparé. C'est le geste que l'erreur d'envoi désigne explicitement.
 */
export function useRegenerateQuoteRequestSpec() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ quoteRequestId, specId }: { quoteRequestId: string; specId: string }) =>
      api.post<QuoteRequestSpecRow>(
        `/v1/quote-requests/${quoteRequestId}/spec/${specId}/regenerate`,
        {},
      ),
    onSuccess: (_d, { quoteRequestId }) => {
      void qc.invalidateQueries({ queryKey: ["quote-requests", "spec", quoteRequestId] });
      void qc.invalidateQueries({ queryKey: ["quote-requests", "readiness", quoteRequestId] });
    },
  });
}
